import { Navigate, useLocation } from "react-router-dom";
import { Spinner } from "@/shared/ui/spinner";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/features/auth/AuthContext";
import { postAuthPath } from "@/features/auth/postAuthPath";
import { PROFILE_NAME_PATH, needsProfileName, profileNamePathFor } from "@/features/auth/profileName";
import { useWorkspace } from "@/features/workspaces/WorkspaceContext";
import {
  isExtensionConnectRedirect,
  loginPathForRedirect,
  postLoginRedirect,
} from "@/lib/extensionConnect";
import { safeRedirectPath } from "@/lib/errors";

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { status, user } = useAuth();
  const { t } = useI18n();
  const location = useLocation();
  const destination = `${location.pathname}${location.search}${location.hash}`;

  if (status === "loading") {
    return <Spinner label={t("auth.checkingSession")} />;
  }

  if (status === "anonymous") {
    return <Navigate to={loginPathForRedirect(destination)} replace />;
  }

  // Every signed-in page sits behind this guard, so a user without a name
  // cannot reach any of them until they have saved one.
  if (needsProfileName(user) && location.pathname !== PROFILE_NAME_PATH) {
    return <Navigate to={profileNamePathFor(destination)} replace />;
  }

  return children;
}

export function WorkspaceReadyRoute({ children }: { children: React.ReactNode }) {
  const { status, user } = useAuth();
  const { t } = useI18n();
  const { workspaces, ready } = useWorkspace();

  if (status === "loading" || (status === "authenticated" && !ready)) {
    return <Spinner label={t("auth.checkingSetup")} />;
  }

  if (status === "authenticated" && postAuthPath(user, workspaces) === "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
}

export function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { status, user } = useAuth();
  const { t } = useI18n();
  const { workspaces, ready } = useWorkspace();
  const location = useLocation();
  const stateRedirect =
    typeof location.state === "object" &&
    location.state !== null &&
    "from" in location.state &&
    typeof location.state.from === "string"
      ? location.state.from
      : "/";
  const fallback = safeRedirectPath(stateRedirect);
  const redirect = postLoginRedirect(location.search, fallback);

  if (status === "loading" || (status === "authenticated" && !ready)) {
    return <Spinner label={t("auth.checkingSession")} />;
  }

  if (status === "authenticated") {
    if (isExtensionConnectRedirect(redirect)) {
      return <Navigate to={redirect} replace />;
    }
    return <Navigate to={postAuthPath(user, workspaces, redirect)} replace />;
  }

  return children;
}
